
import React from 'react';
import { X, Table, FileText, Loader2, AlertCircle, Calendar, Hash, Binary, Type, Sigma } from 'lucide-react';
import { apiFetch } from '@/api';

export const DataPreviewModal = ({ isOpen, onClose, dataset }) => {
    // Helper to get Icon based on enriched schema
    const getColumnTypeIcon = (colName) => {
        if (!dataset) return Type;

        const lowerCol = colName.toLowerCase();

        // Check Dimensions
        const dim = dataset.dimensions?.find(d => d.name.toLowerCase() === lowerCol);
        if (dim) {
            if (dim.type === 'date') return Calendar;
            if (dim.type === 'boolean') return Binary;
            if (dim.type === 'number') return Hash;
            return Type; // String
        }

        // Check Measures
        const msr = dataset.measures?.find(m => m.name.toLowerCase() === lowerCol);
        if (msr) return Sigma;

        return Type;
    };

    // Moved conditional return to after hooks
    // if (!isOpen) return null;

    // State for live data
    const [rows, setRows] = React.useState([]);
    // Columns should come from API for accuracy, but fallback to dataset defs
    const [apiColumns, setApiColumns] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);

    // Dynamic Columns: Use API columns if available, else dataset metadata
    const activeColumns = React.useMemo(() => {
        if (apiColumns.length > 0) return apiColumns;
        return [
            ...(dataset?.dimensions?.map(d => d.name) || []),
            ...(dataset?.measures?.map(m => m.name) || [])
        ];
    }, [dataset, apiColumns]);

    // Adaptive Row Normalization (Handle Array vs Object response)
    const normalizedRows = React.useMemo(() => {
        if (!rows || rows.length === 0) return [];

        // Check first row format
        const firstRow = rows[0];
        if (Array.isArray(firstRow)) {
            // DUCKDB (Array) Mode
            return rows.map(r => {
                const obj = {};
                activeColumns.forEach((col, idx) => {
                    obj[col] = r[idx];
                });
                return obj;
            });
        }
        // SQL (Object) Mode
        return rows;
    }, [rows, activeColumns]);


    React.useEffect(() => {
        if (!isOpen || !dataset?.id) return;

        const fetchPreview = async () => {
            setLoading(true);
            setError(null);
            setRows([]);
            setApiColumns([]);

            try {
                // Determine Context
                const isTenantContext = window.location.pathname.startsWith('/tenant');
                const base = isTenantContext ? '/tenant-portal/datasets' : '/datasets';

                // Call Unified Preview Endpoint (Handles DuckDB/Parquet vs Live SQL internally)
                // console.log(`[DataPreview] Fetching preview for ${dataset.id}`);
                const data = await apiFetch(`${base}/${dataset.id}/preview?limit=50`);

                if (data) {
                    // API returns { columns: string[], rows: any[] }
                    if (data.columns) setApiColumns(data.columns);
                    if (data.rows) setRows(data.rows);
                }
            } catch (err) {
                console.error("[DataPreview] Failed to fetch:", err);
                setError(err.message || "Failed to load data preview");
            } finally {
                setLoading(false);
            }
        };

        fetchPreview();
    }, [isOpen, dataset?.id]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                            <Table className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                Data Preview
                                <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-[10px] rounded-full uppercase tracking-wider font-bold">
                                    {dataset?.name || 'No Dataset'}
                                </span>
                            </h2>
                            <p className="text-xs text-slate-500">Previewing first 50 rows of raw data</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Table Content */}
                <div className="flex-1 overflow-auto relative min-h-[200px]">
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-20">
                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                        </div>
                    )}

                    {error && (
                        <div className="flex flex-col items-center justify-center h-full text-red-500 p-8">
                            <AlertCircle className="w-8 h-8 mb-2" />
                            <p>{error}</p>
                        </div>
                    )}

                    {!loading && !error && dataset ? (
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
                                <tr>
                                    {activeColumns.map((col, idx) => {
                                        const Icon = getColumnTypeIcon(col);
                                        return (
                                            <th key={idx} className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Icon className="w-3 h-3 text-blue-400" />
                                                    {col}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {normalizedRows.map((row, rIdx) => (
                                    <tr key={rIdx} className="hover:bg-slate-50/80 transition-colors">
                                        {activeColumns.map((col, cIdx) => (
                                            <td key={cIdx} className="px-6 py-3 text-sm text-slate-600 whitespace-nowrap border-b border-slate-50">
                                                {row[col]?.toString() || '-'}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                            <FileText className="w-12 h-12 mb-4 opacity-50" />
                            <p>No dataset selected.</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};
